import { useCallback, useEffect, useRef, useState } from 'react';
import { getStatsByPeriod, getStatsYears } from '../../api';
import { PilotPercent } from '../../types';
import { useAircraft } from '../../aircraft/AircraftContext';
import { onAircraftChange } from '../../aircraft/aircraftStore';
import PageBackLink from '../../components/PageBackLink/PageBackLink';
import PilotBadge from '../../components/PilotBadge/PilotBadge';
import { useFeedback } from '../../hooks/useFeedback';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatPercent } from '../../utils/formatMetric';
import FlightsHistorySection from './FlightsHistorySection';
import './StatsPage.css';

const StatsPage = () => {
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [periodStats, setPeriodStats] = useState<{
    total_hours: number;
    flights_count: number;
    landings: number;
    pilots: PilotPercent[];
  } | null>(null);
  const { aircraftId, dataRevision } = useAircraft();
  const { showError, feedbackNode } = useFeedback();
  const periodReqId = useRef(0);

  const loadPeriodStats = useCallback(async () => {
    const reqId = ++periodReqId.current;
    const year = selectedYear;
    const month = selectedMonth;
    try {
      const res = await getStatsByPeriod(year, month);
      if (reqId !== periodReqId.current) return;
      setPeriodStats(res.data);
    } catch (error) {
      if (reqId !== periodReqId.current) return;
      setPeriodStats(null);
      showError(
        getApiErrorMessage(error, 'Не удалось загрузить статистику за период')
      );
    }
  }, [selectedYear, selectedMonth, showError]);

  const loadYears = useCallback(async () => {
    try {
      const res = await getStatsYears();
      const years = res.data.years || [];
      setAvailableYears(years);
      setSelectedYear((prev) =>
        prev != null && !years.includes(prev) ? null : prev
      );
    } catch (error) {
      setAvailableYears([]);
      showError(
        getApiErrorMessage(error, 'Не удалось загрузить годы статистики')
      );
    }
  }, [showError]);

  useEffect(() => {
    void loadYears();
  }, [aircraftId, dataRevision, loadYears]);

  useEffect(() => {
    void loadPeriodStats();
    return () => {
      periodReqId.current += 1;
    };
  }, [aircraftId, dataRevision, loadPeriodStats]);

  useEffect(
    () =>
      onAircraftChange(() => {
        void loadYears();
        void loadPeriodStats();
      }),
    [loadYears, loadPeriodStats]
  );

  const periodTitle = (() => {
    const monthName = selectedMonth
      ? new Date(0, selectedMonth - 1).toLocaleString('ru', { month: 'long' })
      : null;

    if (selectedYear === null && monthName) {
      return `За ${monthName} каждого года`;
    }
    if (selectedYear === null) {
      return 'За всё время в клубе';
    }
    if (monthName) {
      return `За ${monthName} ${selectedYear}`;
    }
    return `За ${selectedYear} год`;
  })();

  return (
    <div className="stats-page">
      {feedbackNode}
      <div className="stats-header">
        <PageBackLink to="/" />
        <h2>Статистика</h2>
      </div>

      <div className="period-stats">
        <div className="period-stats-top">
          <div className="period-stats-info">
            <h3>{periodTitle}</h3>
            {periodStats && periodStats.flights_count > 0 && (
              <>
                <p>
                  <strong>Всего записей:</strong> {periodStats.flights_count}
                </p>
                <p>
                  <strong>Налёт за период:</strong> {periodStats.total_hours} м/ч
                </p>
                <p>
                  <strong>Посадки:</strong> {periodStats.landings}
                </p>
              </>
            )}
          </div>
          <div className="stats-filters">
            <label>
              Год:
              <select
                value={selectedYear ?? ''}
                onChange={(e) =>
                  setSelectedYear(e.target.value ? Number(e.target.value) : null)
                }
              >
                <option value="">Все</option>
                {availableYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Месяц:
              <select
                value={selectedMonth || ''}
                onChange={(e) =>
                  setSelectedMonth(e.target.value ? Number(e.target.value) : null)
                }
              >
                <option value="">Все</option>
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString('ru', { month: 'long' })}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {periodStats &&
          (periodStats.flights_count === 0 ? (
            <p className="period-stats-empty">Нет полётов за выбранный период</p>
          ) : (
            <div className="stats-table-wrap">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Пилот</th>
                    <th>Моточасы</th>
                    <th>Процент</th>
                  </tr>
                </thead>
                <tbody>
                  {periodStats.pilots.map((p) => (
                    <tr key={p.pilot}>
                      <td>
                        <PilotBadge name={p.pilot} color={p.color} />
                      </td>
                      <td>{p.hours}</td>
                      <td>{formatPercent(p.percent)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </div>

      <FlightsHistorySection />
    </div>
  );
};

export default StatsPage;
